// Huawei50kError.js
// Huawei SUN2000 50K 계열 Modbus 문서의 실제 레지스터와 RegisterIvPublic 필드를 연결한다.
// bitmap : 각 비트가 독립적인 상태/알람을 의미한다.
// enum   : 레지스터 전체 값이 하나의 장치 상태 코드를 의미한다.
// code   : 현재 알람 중 우선순위가 가장 높은 Fault code를 의미한다.

import "./OcipError.css";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Table } from "react-bootstrap";
import { getIvFieldValue } from "../../utils/ivErrorDataUtils";

/**
 * 필드 매핑
 * 1 state  → Standalone teleindication (32000)
 * 2 Warn   → Operating status (32002)
 * 3 fault0 → Alarm 1 (32008)
 * 4 fault1 → Alarm 2 (32009)
 * 5 fault2 → Alarm 3 (32010)
 * 6 mode   → Device status (32089)
 * 7 Pfault → Fault code (32090)
 */
const AlarmModalData = {
  1: {
    title: "단독 원격표시",
    address: 32000,
    field: "state",
    type: "bitmap",
    rows: [
      { bit: "Bit0", desc: "대기", code: "0x01", isFault: false },
      { bit: "Bit1", desc: "계통 연결", code: "0x02", isFault: false },
      { bit: "Bit2", desc: "정상 계통 연결", code: "0x04", isFault: false },
      { bit: "Bit3", desc: "출력제한으로 인한 감발 운전", code: "0x08", isFault: false },
      { bit: "Bit4", desc: "내부 원인으로 인한 감발 운전", code: "0x10", isFault: false },
      { bit: "Bit5", desc: "정상 정지", code: "0x20", isFault: false },
      { bit: "Bit6", desc: "고장 정지", code: "0x40", isFault: true },
      { bit: "Bit7", desc: "출력제한 정지", code: "0x80", isFault: false },
    ],
  },

  2: {
    title: "운전 상태",
    address: 32002,
    field: "Warn",
    type: "bitmap",
    rows: [
      {
        bit: "Bit0",
        desc: "잠금 상태 (0: 잠김 / 1: 해제)",
        code: "0x01",
        isFault: false,
        zeroLabel: "잠김",
        oneLabel: "해제",
      },
      {
        bit: "Bit1",
        desc: "PV 연결 상태 (0: 미연결 / 1: 연결)",
        code: "0x02",
        isFault: false,
        zeroLabel: "미연결",
        oneLabel: "연결",
      },
      {
        bit: "Bit2",
        desc: "DSP 데이터 수집 (0: 아니오 / 1: 예)",
        code: "0x04",
        isFault: false,
        zeroLabel: "아니오",
        oneLabel: "예",
      },
    ],
  },

  3: {
    title: "알람 1 비트맵",
    address: 32008,
    field: "fault0",
    type: "bitmap",
    rows: [
      { bit: "Bit0", desc: "스트링 입력 고전압", code: "0x01", isFault: true },
      { bit: "Bit1", desc: "DC 아크 고장", code: "0x02", isFault: true },
      { bit: "Bit2", desc: "스트링 역결선", code: "0x04", isFault: true },
      { bit: "Bit3", desc: "스트링 역전류", code: "0x08", isFault: true },
      { bit: "Bit4", desc: "스트링 전력 이상", code: "0x10", isFault: true },
      { bit: "Bit5", desc: "AFCI 자가진단 실패", code: "0x20", isFault: true },
      { bit: "Bit6", desc: "상전선-접지 단락", code: "0x40", isFault: true },
      { bit: "Bit7", desc: "계통 상실", code: "0x80", isFault: true },
      { bit: "Bit8", desc: "계통 저전압", code: "0x100", isFault: true },
      { bit: "Bit9", desc: "계통 과전압", code: "0x200", isFault: true },
      { bit: "Bit10", desc: "계통 전압 불평형", code: "0x400", isFault: true },
      { bit: "Bit11", desc: "계통 과주파수", code: "0x800", isFault: true },
      { bit: "Bit12", desc: "계통 저주파수", code: "0x1000", isFault: true },
      { bit: "Bit13", desc: "계통 주파수 불안정", code: "0x2000", isFault: true },
      { bit: "Bit14", desc: "출력 과전류", code: "0x4000", isFault: true },
      { bit: "Bit15", desc: "출력 전류 DC 성분 과다", code: "0x8000", isFault: true },
    ],
  },

  4: {
    title: "알람 2 비트맵",
    address: 32009,
    field: "fault1",
    type: "bitmap",
    rows: [
      { bit: "Bit0", desc: "잔류전류 이상", code: "0x01", isFault: true },
      { bit: "Bit1", desc: "접지 이상", code: "0x02", isFault: true },
      { bit: "Bit2", desc: "절연저항 저하", code: "0x04", isFault: true },
      { bit: "Bit3", desc: "과온", code: "0x08", isFault: true },
      { bit: "Bit4", desc: "장치 고장", code: "0x10", isFault: true },
      { bit: "Bit5", desc: "업그레이드 실패 또는 버전 불일치", code: "0x20", isFault: true },
      { bit: "Bit6", desc: "라이선스 만료", code: "0x40", isFault: true },
      { bit: "Bit7", desc: "모니터링 유닛 이상", code: "0x80", isFault: true },
    ],
  },

  5: {
    title: "알람 3 비트맵",
    address: 32010,
    field: "fault2",
    type: "bitmap",
    rows: [
      { bit: "Bit1", desc: "내장 PID 동작 이상", code: "0x02", isFault: true },
      { bit: "Bit2", desc: "스트링 대지전압 과다", code: "0x04", isFault: true },
    ],
  },

  6: {
    title: "장치 상태",
    address: 32089,
    field: "mode",
    type: "enum",
    rows: [
      { value: 0x0000, label: "대기: 초기화", isFault: false },
      { value: 0x0001, label: "대기: 절연저항 검사", isFault: false },
      { value: 0x0002, label: "대기: 일사량 감지", isFault: false },
      { value: 0x0003, label: "대기: 계통 감지", isFault: false },
      { value: 0x0100, label: "기동 중", isFault: false },
      { value: 0x0200, label: "계통 연계 운전", isFault: false },
      { value: 0x0201, label: "계통 연계: 출력 제한 운전", isFault: false },
      { value: 0x0202, label: "계통 연계: 자체 감발 운전", isFault: false },
      { value: 0x0300, label: "정지: 고장", isFault: true },
      { value: 0x0301, label: "정지: 명령", isFault: false },
      { value: 0x0302, label: "정지: OVGR", isFault: true },
      { value: 0x0303, label: "정지: 통신 두절", isFault: true },
      { value: 0x0304, label: "정지: 출력 제한", isFault: false },
      { value: 0x0305, label: "정지: 수동 기동 대기", isFault: false },
      { value: 0x0306, label: "정지: DC 스위치 OFF", isFault: false },
      { value: 0x0401, label: "계통 급전: cos(Φ)-P 곡선", isFault: false },
      { value: 0x0402, label: "계통 급전: Q-U 곡선", isFault: false },
      { value: 0x0500, label: "스팟 체크 대기", isFault: false },
      { value: 0x0501, label: "스팟 체크 중", isFault: false },
      { value: 0x0600, label: "점검 중", isFault: false },
      { value: 0x0700, label: "AFCI 자가진단", isFault: false },
      { value: 0x0800, label: "IV 스캔", isFault: false },
      { value: 0x0900, label: "DC 입력 감지", isFault: false },
      { value: 0xa000, label: "대기: 일사량 없음", isFault: false },
    ],
  },

  7: {
    title: "고장 코드",
    address: 32090,
    field: "Pfault",
    type: "code",
  },
};

const Huawei50kError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});
  const [openModalNum, setOpenModalNum] = useState(null);

  const closeModal = () => {
    setOpenModalNum(null);
  };

  const getGroupValue = (num) => {
    const modal = AlarmModalData[num];

    if (!modal) {
      return null;
    }

    const raw = getIvFieldValue(
      ivData,
      modal.field,
      SelInverter,
      null
    );

    if (
      raw === null ||
      raw === undefined ||
      raw === ""
    ) {
      return null;
    }

    const value = Number(raw);

    return Number.isFinite(value)
      ? value
      : null;
  };

  const parseBitFlags = (value) => {
    const numberValue = Number(value) || 0;
    const flags = {};

    for (let i = 0; i <= 15; i += 1) {
      flags[`Bit${i}`] = (numberValue >> i) & 1;
    }

    return flags;
  };

  const isBitFault = (row, bitValue) => {
    if (row.faultWhen !== undefined) {
      return Number(bitValue) === Number(row.faultWhen);
    }

    if (row.isFault === false) {
      return false;
    }

    return Number(bitValue) === 1;
  };

  const isAbnormalGroup = (groupNum, value) => {
    const modal = AlarmModalData[groupNum];

    if (!modal || value === null) {
      return false;
    }

    if (modal.type === "code") {
      return Number(value) !== 0;
    }

    if (modal.type === "enum") {
      const matched = modal.rows.find(
        (row) => row.value === Number(value)
      );

      return matched
        ? Boolean(matched.isFault)
        : true;
    }

    const bits = parseBitFlags(value);

    return modal.rows.some((row) => {
      if (
        row.isFault === false &&
        row.faultWhen === undefined
      ) {
        return false;
      }

      return isBitFault(
        row,
        bits[row.bit] ?? 0
      );
    });
  };

  const formatHex = (value) => {
    return Number(value)
      .toString(16)
      .toUpperCase()
      .padStart(4, "0");
  };

  const renderNoDataModal = (modal) => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>
          {modal.title} [{modal.address}]
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        아직 수신된 데이터가 없습니다.
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
        >
          닫기
        </Button>
      </Modal.Footer>
    </>
  );

  const renderCodeModal = (modal, rawValue) => {
    const fault = rawValue !== 0;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.title} [{modal.address}]
            {" "}
            (0x{formatHex(rawValue)})
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Table
            striped
            bordered
            size="sm"
          >
            <thead>
              <tr>
                <th>코드</th>
                <th>16진수</th>
                <th>상태</th>
              </tr>
            </thead>

            <tbody>
              <tr className={fault ? "active-row" : ""}>
                <td>{rawValue}</td>
                <td>0x{formatHex(rawValue)}</td>
                <td>
                  {fault ? "고장" : "정상"}
                </td>
              </tr>
            </tbody>
          </Table>

          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              color: "#666",
            }}
          >
            현재 발생한 알람 중 최우선 고장 코드입니다.
            상세 의미는 Huawei 알람 설명서를 참고하세요.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
          >
            닫기
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderEnumModal = (modal, rawValue) => {
    const matchedRow = modal.rows.find(
      (row) => row.value === rawValue
    );

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.title} [{modal.address}]
            {" "}
            (0x{formatHex(rawValue)})
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!matchedRow && (
            <div
              style={{
                marginBottom: 10,
                padding: 10,
                background: "#fff3cd",
                border: "1px solid #ffe69c",
                borderRadius: 4,
              }}
            >
              문서에 정의되지 않은 상태 코드입니다.
            </div>
          )}

          <Table
            striped
            bordered
            size="sm"
          >
            <thead>
              <tr>
                <th>값</th>
                <th>설명</th>
                <th>상태</th>
              </tr>
            </thead>

            <tbody>
              {modal.rows.map((row) => {
                const active = row.value === rawValue;
                const fault = active && row.isFault;

                return (
                  <tr
                    key={row.value}
                    className={
                      active
                        ? "active-row"
                        : ""
                    }
                  >
                    <td>
                      0x{formatHex(row.value)}
                    </td>

                    <td>{row.label}</td>

                    <td>
                      {!active
                        ? "-"
                        : fault
                          ? "고장"
                          : "활성"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
          >
            닫기
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderBitmapModal = (modal, rawValue) => {
    const bits = parseBitFlags(rawValue);

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal.title} [{modal.address}]
            {" "}
            (0x{formatHex(rawValue)})
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Table
            striped
            bordered
            size="sm"
          >
            <thead>
              <tr>
                <th>비트</th>
                <th>설명</th>
                <th>마스크</th>
                <th>값</th>
                <th>상태</th>
              </tr>
            </thead>

            <tbody>
              {modal.rows.map((row) => {
                const bitValue = bits[row.bit] ?? 0;
                const fault = isBitFault(row, bitValue);

                const active =
                  bitValue === 1 ||
                  (
                    row.faultWhen !== undefined &&
                    bitValue === row.faultWhen
                  );

                let status = "정상";

                if (
                  row.zeroLabel !== undefined ||
                  row.oneLabel !== undefined
                ) {
                  status =
                    bitValue === 1
                      ? row.oneLabel
                      : row.zeroLabel;
                } else if (fault) {
                  status = "고장";
                } else if (active) {
                  status = "활성";
                }

                return (
                  <tr
                    key={row.bit}
                    className={
                      fault
                        ? "active-row"
                        : ""
                    }
                  >
                    <td>{row.bit}</td>
                    <td>{row.desc}</td>
                    <td>{row.code}</td>
                    <td>{bitValue}</td>
                    <td>{status}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
          >
            닫기
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderModalContent = () => {
    const modal = AlarmModalData[openModalNum];

    if (!modal) {
      return null;
    }

    const rawValue = getGroupValue(openModalNum);

    if (rawValue === null) {
      return renderNoDataModal(modal);
    }

    if (modal.type === "code") {
      return renderCodeModal(
        modal,
        rawValue
      );
    }

    if (modal.type === "enum") {
      return renderEnumModal(
        modal,
        rawValue
      );
    }

    return renderBitmapModal(
      modal,
      rawValue
    );
  };

  const items = [
    {
      label: "단독 원격표시",
      num: 1,
    },
    {
      label: "운전 상태",
      num: 2,
    },
    {
      label: "알람 1 비트맵",
      num: 3,
    },
    {
      label: "알람 2 비트맵",
      num: 4,
    },
    {
      label: "알람 3 비트맵",
      num: 5,
    },
    {
      label: "장치 상태",
      num: 6,
    },
    {
      label: "고장 코드",
      num: 7,
    },
  ];

  return (
    <div className="status-list-container">
      <div className="status-list">
        {items.map(({ label, num }) => {
          const value = getGroupValue(num);
          const hasData = value !== null;

          const abnormal =
            hasData &&
            isAbnormalGroup(
              num,
              value
            );

          return (
            <div
              key={num}
              className="status-item"
            >
              <span className="status-value">
                {label}:{" "}
                {hasData
                  ? value
                  : "-"}
              </span>

              <button
                type="button"
                onClick={() => {
                  setOpenModalNum(num);
                }}
              >
                <span
                  className={
                    abnormal
                      ? "blinking-icon"
                      : ""
                  }
                >
                  {!hasData
                    ? "⚪"
                    : abnormal
                      ? "🔴"
                      : "🟢"}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {openModalNum !== null && (
        <Modal
          show
          onHide={closeModal}
          size="lg"
        >
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default Huawei50kError;